import { useState, useRef } from "react";
import {
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Autocomplete,
  TextField,
  CircularProgress,
  Typography,
} from "@mui/material";
import FancyPaper from "../components/FancyPaper";
import { useApp } from "../hooks/useApp";
import {
  getRequestProductsInventory,
  getRequestProductDistribution,
} from "../services/inventory/InventoryService";

interface ProductOption {
  id: number;
  code: string;
  description: string;
}

interface ProjectQuantity {
  projectId: number;
  projectName: string;
  quantity: number;
}

interface ProductDistribution {
  productId: number;
  code: string;
  description: string;
  warehouseQuantity: number;
  projects: ProjectQuantity[];
}

const ManagementProductDistribution = () => {
  const { setLoading, setErrorMsg } = useApp();
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductOption | null>(
    null
  );
  const [distribution, setDistribution] = useState<ProductDistribution | null>(
    null
  );
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleInputChange = (_event: unknown, value: string) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    if (!value.trim()) {
      setOptions([]);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const data = await getRequestProductsInventory(0, value, 15);
        setOptions(data?.content ?? []);
      } catch (error: any) {
        setErrorMsg && setErrorMsg(error.message);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
  };

  const handleProductChange = async (
    _event: unknown,
    newValue: ProductOption | null
  ) => {
    setSelectedProduct(newValue);
    setDistribution(null);
    if (!newValue) {
      return;
    }
    setLoading && setLoading(true);
    try {
      const data = await getRequestProductDistribution(newValue.id);
      setDistribution(data);
    } catch (error: any) {
      setErrorMsg && setErrorMsg(error.message);
    } finally {
      setLoading && setLoading(false);
    }
  };

  return (
    <FancyPaper
      pagetitle="Distribución de productos en proyectos"
      prevpage="Distribución de productos en proyectos"
    >
      <Grid container spacing={8} sx={{ pb: 8 }}>
        <Grid item md={5} sm={7} xs={12}>
          <Autocomplete
            value={selectedProduct}
            onChange={handleProductChange}
            onInputChange={handleInputChange}
            options={options}
            loading={searchLoading}
            getOptionLabel={(option) =>
              option.code ? `${option.code} - ${option.description}` : ""
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar producto por código o nombre"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
      </Grid>

      {distribution && (
        <>
          <Typography
            variant="h6"
            align="center"
            sx={{ mb: 4 }}
          >
            {distribution.description}
            {distribution.code ? ` - ${distribution.code}` : ""}
          </Typography>
          <TableContainer>
            <Table sx={{ minWidth: 400 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>{"Proyecto"}</TableCell>
                  <TableCell align="center">{"Cantidad"}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell scope="row">{"Inventario en bodega"}</TableCell>
                  <TableCell align="center">
                    {distribution.warehouseQuantity}
                  </TableCell>
                </TableRow>
                {distribution.projects.map((project) => (
                  <TableRow key={project.projectId}>
                    <TableCell scope="row">{project.projectName}</TableCell>
                    <TableCell align="center">{project.quantity}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>{"Total"}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    {distribution.warehouseQuantity +
                      distribution.projects.reduce(
                        (acc, project) => acc + project.quantity,
                        0
                      )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </FancyPaper>
  );
};

export default ManagementProductDistribution;
